import React, { useState, useEffect, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Card, CardBody, Button, Spinner, Badge } from 'reactstrap';
import moment from 'moment';
import WorkoutPrescriptionService from '../../../services/workout_prescription';
import ThermalPrintableWorkout from './ThermalPrintableWorkout';
import { useTenant } from '../../../hooks/useTenant';

const KioskWorkoutsTab = ({ member, tenantLogo }) => {
    const { idTenant, tenantName } = useTenant();
    const [prescriptions, setPrescriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedWorkout, setSelectedWorkout] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [weekOffset, setWeekOffset] = useState(0);
    const printRef = useRef(null);

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: `Treino_${member?.firstName || 'Aluno'}`,
        pageStyle: `
            @page {
                size: 80mm auto; /* Largura de impressora térmica 80mm */
                margin: 0mm;
            }
            @media print {
                body {
                    -webkit-print-color-adjust: exact;
                }
            }
        `,
        onAfterPrint: () => {
            setSelectedWorkout(null);
            setShowPreview(false);
        },
    });

    useEffect(() => {
        const fetchWorkouts = async () => {
            if (!member?.id || !idTenant) return;
            setLoading(true);
            try {
                // Busca prescrições ativas do aluno
                const data = await WorkoutPrescriptionService.getActiveByMember(idTenant, member.id);

                // Para cada prescrição, busca o progresso de sessões
                const enrichedData = await Promise.all(
                    (data || []).map(async (prescription) => {
                        try {
                            const sessions = await WorkoutPrescriptionService.getSessionsByPrescription(
                                idTenant, member.id, prescription.id
                            );
                            const completedCount = sessions.filter(s => s.status === "completed").length;
                            const pendingCount = sessions.filter(s => s.status === "pending").length;
                            return {
                                ...prescription,
                                _sessions: sessions,
                                _completedCount: completedCount,
                                _pendingCount: pendingCount,
                                _totalSessions: sessions.length,
                            };
                        } catch {
                            return { ...prescription, _sessions: [], _completedCount: 0, _pendingCount: 0, _totalSessions: 0 };
                        }
                    })
                );

                setPrescriptions(enrichedData);
            } catch (error) {
                console.error("KioskWorkoutsTab -> Erro ao buscar treinos:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchWorkouts();
    }, [member?.id, idTenant]);

    const handlePrintClick = async (workout, prescription, workoutIndex) => {
        // Determina o workoutType (pode estar no bloco, no workout, ou na prescrição)
        const resolvedType = workout.workoutType 
            || (workout.blocks?.[0]?.workoutType) 
            || prescription.workoutType 
            || "strength";

        // Monta o objeto de treino para impressão
        const printWorkout = {
            ...workout,
            title: workout.name || workout.title || `Treino ${String.fromCharCode(65 + workoutIndex)}`,
            workoutType: resolvedType,
            teacherName: prescription.teacherName,
        };

        setSelectedWorkout(printWorkout);

        // Consome a próxima sessão pendente desse treino específico
        if (prescription._sessions && prescription._sessions.length > 0) {
            // Verifica se JÁ completou uma sessão deste treino HOJE (evita consumir ao reimprimir)
            const today = new Date().toISOString().split("T")[0];
            const alreadyDoneToday = (prescription._sessions || []).some(s => {
                if (s.workoutIndex !== workoutIndex) return false;
                if (s.status !== "completed") return false;
                // Verifica se completedAt é de hoje
                const completedDate = s.completedAt?.toDate 
                    ? s.completedAt.toDate().toISOString().split("T")[0]
                    : s.completedAt instanceof Date 
                        ? s.completedAt.toISOString().split("T")[0]
                        : null;
                return completedDate === today;
            });

            if (!alreadyDoneToday) {
                try {
                    const nextSession = await WorkoutPrescriptionService.getNextPendingSession(
                        idTenant, member.id, prescription.id, workoutIndex
                    );

                    if (nextSession) {
                        await WorkoutPrescriptionService.completeSession(
                            idTenant, member.id, prescription.id, nextSession.id, "kiosk_print"
                        );

                        // Atualiza o estado local imediatamente (sem precisar recarregar)
                        setPrescriptions(prev => prev.map(p => {
                            if (p.id !== prescription.id) return p;
                            return {
                                ...p,
                                _completedCount: (p._completedCount || 0) + 1,
                                _sessions: (p._sessions || []).map(s =>
                                    s.id === nextSession.id
                                        ? { ...s, status: "completed", completedAt: new Date(), completedVia: "kiosk_print" }
                                        : s
                                ),
                            };
                        }));
                    }
                } catch (error) {
                    console.error("KioskWorkoutsTab -> Erro ao consumir sessão:", error);
                }
            }
        }

        // Mostra a prévia ao invés de imprimir direto
        setTimeout(() => {
            setShowPreview(true);
        }, 100);
    };

    const handleConfirmPrint = () => {
        if (handlePrint) {
            handlePrint();
        }
    };

    const handleClosePreview = () => {
        setShowPreview(false);
        setSelectedWorkout(null);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
                <Spinner color="primary" />
            </div>
        );
    }

    if (prescriptions.length === 0) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center text-muted text-center p-5" style={{ minHeight: "200px" }}>
                <i className="mdi mdi-clipboard-text-off-outline display-4 d-block mb-3 opacity-50" />
                <h5 className="fw-bold text-dark">Nenhum Treino</h5>
                <p className="mb-0">Não há treinos ativos prescritos para você no momento.</p>
            </div>
        );
    }

    // Semana atual com offset
    const weekStart = moment().startOf('isoWeek').add(weekOffset, 'weeks');
    const weekEnd = moment(weekStart).endOf('isoWeek');

    return (
        <div style={{ padding: '16px', marginBottom: '40px' }}>
            <h4 className="fw-bold text-dark mb-3">Meus Treinos Ativos</h4>

            {/* Navegação de Semana */}
            <div className="d-flex align-items-center justify-content-between mb-4 p-3 rounded" style={{ backgroundColor: '#f0f2f6' }}>
                <button
                    className="btn btn-sm btn-outline-primary rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: 40, height: 40 }}
                    onClick={() => setWeekOffset(prev => prev - 1)}
                >
                    <i className="mdi mdi-chevron-left font-size-22" />
                </button>
                <div className="text-center">
                    <h6 className="mb-0 fw-bold text-dark font-size-15">
                        {weekOffset === 0 ? "Esta Semana" : weekOffset === -1 ? "Semana Passada" : weekOffset === 1 ? "Próxima Semana" : `Semana ${weekStart.format("DD/MM")} - ${weekEnd.format("DD/MM")}`}
                    </h6>
                    <small className="text-muted">
                        {weekStart.format("DD/MM/YYYY")} — {weekEnd.format("DD/MM/YYYY")}
                    </small>
                </div>
                <button
                    className="btn btn-sm btn-outline-primary rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: 40, height: 40 }}
                    onClick={() => setWeekOffset(prev => prev + 1)}
                >
                    <i className="mdi mdi-chevron-right font-size-22" />
                </button>
            </div>
            
            {prescriptions.map((prescription, pIndex) => {
                const hasSessions = prescription._totalSessions > 0;
                const progressPercent = hasSessions
                    ? Math.round((prescription._completedCount / prescription._totalSessions) * 100)
                    : 0;

                return (
                    <div key={prescription.id} className="mb-5">
                        {/* Cabeçalho da Prescrição (Pasta) */}
                        <div className="d-flex align-items-center mb-3 pb-2 border-bottom">
                            <i className="mdi mdi-folder-open text-primary font-size-24 me-2"></i>
                            <div className="flex-grow-1">
                                <h5 className="fw-bold text-dark mb-0">{prescription.templateTitle || 'Prescrição de Treino'}</h5>
                                <p className="text-muted font-size-12 mb-0">
                                    Prescrito em: {prescription.prescribedAt ? moment(prescription.prescribedAt).format('DD/MM/YYYY') : '-'}
                                    {prescription.teacherName && ` por ${prescription.teacherName}`}
                                </p>
                                {/* Barra de progresso de sessões */}
                                {hasSessions && (
                                    <div className="d-flex align-items-center gap-2 mt-1">
                                        <div className="flex-grow-1" style={{ height: "6px", backgroundColor: "rgba(0,0,0,0.08)", borderRadius: "3px", maxWidth: "200px" }}>
                                            <div style={{ width: `${progressPercent}%`, height: "100%", backgroundColor: progressPercent >= 100 ? "#28a745" : "#3b5de7", borderRadius: "3px", transition: "width 0.3s ease" }} />
                                        </div>
                                        <span className="font-size-11 text-muted fw-medium">
                                            {prescription._completedCount}/{prescription._totalSessions} sessões
                                        </span>
                                    </div>
                                )}
                            </div>
                            {pIndex === 0 && <Badge color="success" className="ms-auto px-2 py-1 font-size-12">Mais Recente</Badge>}
                        </div>

                        {/* Descobre qual é o PRÓXIMO treino global (o treino "de hoje") */}
                        {(() => {
                            const allSessions = prescription._sessions || [];
                            const globalNextPending = allSessions
                                .filter(s => s.status === "pending")
                                .sort((a, b) => (a.sessionNumber || 0) - (b.sessionNumber || 0))[0];
                            // Se não há pendente, verifica o último feito hoje
                            const today = new Date().toISOString().split("T")[0];
                            const lastDoneToday = allSessions
                                .filter(s => s.status === "completed" && s.completedAt)
                                .sort((a, b) => (b.sessionNumber || 0) - (a.sessionNumber || 0))
                                .find(s => {
                                    const d = s.completedAt?.toDate ? s.completedAt.toDate() : s.completedAt instanceof Date ? s.completedAt : null;
                                    return d && d.toISOString().split("T")[0] === today;
                                });
                            const todayWorkoutIndex = globalNextPending?.workoutIndex ?? lastDoneToday?.workoutIndex ?? -1;

                            return (<div className="row g-3">{(prescription.sessions || []).map((workout, index) => {
                                // Se o professor não deu nome ao treino, nomeamos como "Treino A, B, C..."
                                const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
                                const workoutTitle = workout.name?.trim() || workout.title?.trim() || `Treino ${alphabet[index] || (index + 1)}`;
                                
                                // Calcula quantos exercícios tem no total
                                const totalExercises = (workout.blocks || []).reduce((acc, block) => acc + (block.sets?.length || 0), 0);

                                // Progresso individual deste treino (por workoutIndex)
                                const workoutSessions = allSessions.filter(s => s.workoutIndex === index);
                                const workoutCompleted = workoutSessions.filter(s => s.status === "completed").length;
                                const workoutTotal = workoutSessions.length;

                                // Sessões NESTA SEMANA
                                const weekStartStr = weekStart.format("YYYY-MM-DD");
                                const weekEndStr = weekEnd.format("YYYY-MM-DD");

                                const thisWeekSessions = workoutSessions.filter(s =>
                                    s.scheduledDate && s.scheduledDate >= weekStartStr && s.scheduledDate <= weekEndStr
                                );
                                const nextSession = thisWeekSessions[0] || null;
                                
                                const isToday = index === todayWorkoutIndex && weekOffset === 0;
                                const isAllDone = thisWeekSessions.length > 0 && thisWeekSessions.every(s => s.status === "completed");
                                const hasSessionThisWeek = thisWeekSessions.length > 0;
                                const scheduledDateBR = nextSession?.scheduledDate
                                    ? (() => { const p = nextSession.scheduledDate.split("-"); return `${p[2]}/${p[1]}/${p[0]}`; })()
                                    : (thisWeekSessions[0]?.scheduledDate ? (() => { const p = thisWeekSessions[0].scheduledDate.split("-"); return `${p[2]}/${p[1]}/${p[0]}`; })() : null);

                                // Não mostra card se não tem sessão nesta semana
                                if (!hasSessionThisWeek) return null;

                                return (
                                    <div className="col-12 col-md-6 col-lg-4" key={`${prescription.id}-${index}`}>
                                        {/* Data acima do card: só para hoje ou finalizados */}
                                        {(isToday || isAllDone) ? (
                                            <div className={`text-center mb-2 font-size-12 fw-bold ${isAllDone ? "text-success" : "text-primary"}`}>
                                                <i className={`mdi ${isAllDone ? "mdi-check-circle" : "mdi-calendar-clock"} me-1`} />
                                                {isToday && !isAllDone ? "TREINO DE HOJE" : scheduledDateBR || ""}
                                                {isAllDone && (
                                                    <span className="ms-1 text-success">
                                                        • Finalizado
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="mb-2 font-size-12" style={{ visibility: "hidden" }}>&nbsp;</div>
                                        )}
                                        <Card
                                            className={`border-0 h-100 ${isToday ? "shadow-lg" : "shadow-sm"}`}
                                            style={{
                                                transition: "transform 0.2s, box-shadow 0.2s",
                                                borderRadius: isToday ? "16px" : "12px",
                                                borderTop: `${isToday ? "6px" : "4px"} solid ${isAllDone && !isToday ? "#28a745" : isToday ? "#3b5de7" : "#001D3D"}`,
                                                backgroundColor: isToday ? (isAllDone ? "#e8f5e9" : "#eef2ff") : isAllDone ? "#f6fef6" : "white",
                                                opacity: isAllDone && !isToday ? 0.85 : 1,
                                            }}
                                        >
                                            <CardBody className={`d-flex flex-column ${isToday ? "p-4 px-5" : "p-4"}`}>
                                                <div className="mb-3">
                                                    <div className="d-flex align-items-center justify-content-between mb-1">
                                                        <h5 className={`fw-bold mb-0 ${isToday ? "text-primary" : isAllDone ? "text-success" : "text-dark"}`} style={{ fontSize: isToday ? "22px" : "18px" }}>
                                                            {workoutTitle}
                                                        </h5>
                                                        {isAllDone ? (
                                                            <span className="badge bg-success-subtle text-success px-2 py-1 font-size-11">
                                                                <i className="mdi mdi-check-all me-1" />Concluído
                                                            </span>
                                                        ) : isToday ? (
                                                            <span className="badge bg-primary px-3 py-2 font-size-13">
                                                                <i className="mdi mdi-fire me-1" />HOJE
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    <p className="text-muted font-size-13 mb-0">
                                                        <i className="mdi mdi-dumbbell me-1"></i>
                                                        {totalExercises} exercício(s)
                                                    </p>
                                                    {/* Progresso de sessões deste treino */}
                                                    {workoutTotal > 0 && (
                                                        <p className="font-size-12 mb-0 mt-1" style={{ color: isAllDone ? "#28a745" : "#6c757d" }}>
                                                            <i className={`mdi ${isAllDone ? 'mdi-check-circle' : 'mdi-progress-clock'} me-1`}></i>
                                                            {isAllDone ? `${workoutTotal}/${workoutTotal} sessões concluídas` : `${workoutCompleted}/${workoutTotal} sessões feitas`}
                                                        </p>
                                                    )}
                                                </div>

                                                {workout.description && (
                                                    <p className="text-muted font-size-13 mb-3" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                        {workout.description}
                                                    </p>
                                                )}

                                                {/* Lista de exercícios */}
                                                <div className="mb-3" style={{ maxHeight: "200px", overflowY: "auto", opacity: isAllDone ? 0.5 : 1 }}>
                                                    {(workout.blocks || []).map((block, bIdx) => (
                                                        <div key={bIdx}>
                                                            {block.name && (
                                                                <p className="font-size-11 fw-bold text-uppercase text-primary mb-1 mt-2">
                                                                    {block.name}
                                                                </p>
                                                            )}
                                                            {(block.sets || []).map((set, sIdx) => (
                                                                <div key={sIdx} className="d-flex align-items-center py-1 border-bottom" style={{ gap: "6px" }}>
                                                                    <span className="font-size-11 text-muted" style={{ width: "16px", flexShrink: 0 }}>
                                                                        {sIdx + 1}.
                                                                    </span>
                                                                    <span className="font-size-12 fw-medium text-dark flex-grow-1 text-truncate">
                                                                        {set.exercise || set.name || "Exercício"}
                                                                    </span>
                                                                    <span className="font-size-11 text-muted flex-shrink-0">
                                                                        {set.sets && set.reps ? `${set.sets}×${set.reps}` : set.distance || set.duration || ""}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ))}
                                                </div>

                                                <div className="mt-auto pt-3 text-end">
                                                    <Button
                                                        color={isAllDone ? "success" : isToday ? "primary" : "secondary"}
                                                        outline
                                                        className="rounded-circle d-inline-flex align-items-center justify-content-center shadow-none p-0"
                                                        onClick={() => handlePrintClick(workout, prescription, index)}
                                                        style={{ width: "40px", height: "40px" }}
                                                        title="Imprimir Ficha"
                                                    >
                                                        <i className="mdi mdi-printer font-size-18"></i>
                                                    </Button>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </div>
                                );
                            })}</div>);
                        })()}
                    </div>
                );
            })}

            {/* Hidden component for printing */}
            <div style={{ position: "absolute", overflow: "hidden", height: 0, width: 0 }}>
                <div ref={printRef}>
                    <ThermalPrintableWorkout 
                        workout={selectedWorkout} 
                        tenantName={tenantName} 
                        memberName={member?.fullName} 
                        tenantLogo={tenantLogo}
                    />
                </div>
            </div>

            {/* Modal de Prévia */}
            {showPreview && selectedWorkout && (
                <div
                    style={{
                        position: "fixed",
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.6)",
                        zIndex: 9999,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                    onClick={handleClosePreview}
                >
                    <div
                        style={{
                            backgroundColor: "#fff",
                            borderRadius: "0",
                            padding: "16px",
                            maxHeight: "90vh",
                            overflowY: "auto",
                            width: "340px",
                            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <ThermalPrintableWorkout
                            workout={selectedWorkout}
                            tenantName={tenantName}
                            memberName={member?.fullName}
                            tenantLogo={tenantLogo}
                        />

                        <div className="d-flex justify-content-between mt-3 pt-2 border-top">
                            <button
                                className="btn btn-sm text-muted fw-bold"
                                onClick={handleClosePreview}
                            >
                                Fechar
                            </button>
                            <Button
                                color="primary"
                                className="rounded-circle d-flex align-items-center justify-content-center shadow-none p-0"
                                onClick={handleConfirmPrint}
                                style={{ width: "44px", height: "44px" }}
                                title="Imprimir"
                            >
                                <i className="mdi mdi-printer font-size-20" />
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default KioskWorkoutsTab;
